import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {select, Store} from '@ngrx/store';
import * as fromApplicationSettings from '@app/views/admin/application-settings/store/application-settings.reducers';
import {Observable, of, switchMap, throwError} from 'rxjs';
import {environment} from '@env/environment';
import {catchError, map, takeWhile} from 'rxjs/operators';
import {CookieStorage} from '@app/core/security/cookie-storage';
import {SCHOOL_PASSPORT_AUTH_COOKIE_NAME, User} from '@app/core/model/user';
import {IdentityProvider} from '@app/views/sso/identity-providers/identity-provider';
import {ETXApplication, ETXAsset, ETXOrganization} from '@app/views/sso/sso-portal.model';
import {getAuthenticatedUser} from '@app/app.reducers';
import {OidcSecurityProxyService} from '@app/core/service/oidc.security.proxy.service';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import {
    getPassportLoginIDPWithGradeAsNumber,
    PassportLoginIDP
} from '@app/views/sso/identity-providers/idp-settings/passport-login-idp/passport-login-idp.model';

@Injectable()
export class SSOPortalService {
    constructor(
        private http: HttpClient,
        private cookieStorage: CookieStorage,
        private oidcSecurityProxyService: OidcSecurityProxyService,
        private store: Store<fromApplicationSettings.State>
    ) {
    }

    isAuthenticated(): boolean {
        return !!this.cookieStorage.read(SCHOOL_PASSPORT_AUTH_COOKIE_NAME);
    }

    getETXOrgId(): Observable<number> {
        return this.store.pipe(
            select(getAuthenticatedUser),
            switchMap((user: User) => {
                if (!user?.etxOrgId) {
                    return this.oidcSecurityProxyService.getPayloadFromAccessToken().pipe(
                        switchMap((accessTokenData) => {
                            if (accessTokenData?.org_id) {
                                if (!user) {
                                    user = new User();
                                }
                                user.etxOrgId = <number>accessTokenData?.org_id;
                                return of(user.etxOrgId);
                            } else {
                                return throwError('Could not get SchoolDay Org ID');
                            }
                        })
                    );
                } else {
                    return of(user.etxOrgId);
                }
            })
        );
    }

    getIDPList(etxOrgId: number): Observable<IdentityProvider[]> {
        const params = {orgId: etxOrgId};
        return this.http.get<IdentityProvider[]>(`${environment.etxBaseURI}/auth/idps/available`, {params: params})
            .pipe(
                map((data: Object[]) => this.mapResponseDataList(IdentityProvider, data)),
                catchError(this.handleError)
            );
    }

    changeIDPStatus(denom: string, etxOrgId: number, newStatus: boolean): Observable<IdentityProvider> {
        const params = {
            denom: denom,
            orgId: etxOrgId
        };
        const newStatusValue = (newStatus ? 'enable' : 'disable');
        return this.http.post<IdentityProvider>(`${environment.etxBaseURI}/auth/idps/${newStatusValue}`, null, {params: params})
            .pipe(
                map((data: Object) => this.mapResponseDataItem(IdentityProvider, data)),
                catchError(this.handleError)
            );
    }

    getSchoolPassportContent(): Observable<ETXApplication> {
        return this.http.post<ETXApplication>(`${environment.etxBaseURI}/api/v2/webpassport/passport`, null)
            .pipe(
                map((data: Object) => this.mapResponseDataItem(ETXApplication, data)),
                catchError(this.handleError)
            );
    }

    getQRCodeUrl(): Observable<string> {
        return this.getSchoolPassportContent().pipe(
            map((data: ETXApplication) => {
                let result = '';
                if (data?.assets) {
                    const qrCodeConsoleAsset = data.assets.find((asset: ETXAsset) => asset?.name === 'ID card administration');
                    if (qrCodeConsoleAsset) {
                        result = `${data.ssoUrl}?applicationId=${qrCodeConsoleAsset.appId}&assetId=${qrCodeConsoleAsset.id}`;
                    }
                }
                return result;
            })
        );
    }

    getOrganization(etxOrgId: number): Observable<ETXOrganization> {
        return this.http.get<ETXOrganization>(`${environment.etxBaseURI}/orgadmin/organizations?orgId=${etxOrgId}`)
            .pipe(
                map((data: Object) => this.mapResponseDataItem(ETXOrganization, data)),
                catchError(this.handleError)
            );
    }

    getApplicationSSOLink(idp: IdentityProvider, app: Application, etxOrgId: number, host: string = null): string {
        if (!app.passportSsoService) {
            return '';
        }
        const start = app.passportSsoService.indexOf('_') + 1;
        const end = app.passportSsoService.lastIndexOf('_');
        const etxAppId = app.passportSsoService.substring(start, end);
        return `${host || environment.etxBaseURI}/entrance/${idp.providerIdentifier}/login?autosso=${etxAppId}&orgId=${etxOrgId}`;
    }

    getPassportLoginIDPs(etxOrgId: number): Observable<PassportLoginIDP[]> {
        return this.http.get<PassportLoginIDP[]>(`${environment.etxBaseURI}/account/mfa/management/configs/${etxOrgId}`)
            .pipe(
                map((data: Object[]) => this.mapResponseDataList(PassportLoginIDP, data)),
                catchError(this.handleError)
            );
    }

    savePassportLoginIDPs(passportLoginIDPs: PassportLoginIDP[], districtId: number): Observable<PassportLoginIDP[]> {
        return this.http.post<PassportLoginIDP[]>(
            `${environment.etxBaseURI}/account/mfa/management/configs/${districtId}`,
            getPassportLoginIDPWithGradeAsNumber(passportLoginIDPs)
        ).pipe(catchError(this.handleError));
    }

    private mapResponseDataItem<T>(model: new () => T, item: Object): T {
        return Object.assign(new model(), item);
    }

    private mapResponseDataList<T>(model: new () => T, list: Object[]): T[] {
        return list.map((item: T) => this.mapResponseDataItem(model, item));
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(`Backend returned code ${error.status}, body was: ${error.message}`);
        }
        return throwError(typeof error.error === 'string' && !error.error.includes('<html>') ? error.error : 'Something bad happened, please try again later.');
    }
}
